import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  PvForecastingConfig,
  PvForecastingConfigCreate,
  PvForecastingConfigUpdate,
} from '@core/models';
import { SKIP_GLOBAL_ERROR_SNACKBAR } from '@core/interceptors/http-error.interceptor';
import { environment } from '@root/environments/environment';
import { Observable } from 'rxjs';

/**
 * PV Forecasting Config API. Hosted behind the same API-management gateway under
 * the `/pv-forecasting-config` prefix, so AuthorizeInterceptor still attaches the
 * `Api-Version` and `Authorization` headers.
 *
 * Calls go through HttpClient directly (rather than ApiService) so callers keep
 * the HTTP status code for feedback, and they opt out of the global error
 * snackbar since the card/dialog render their own success and error messages.
 */
@Injectable({
  providedIn: 'root',
})
export class PvForecastingService {
  private readonly baseUrl = `${environment.api.url}/pv-forecasting-config`;

  constructor(private readonly http: HttpClient) {}

  /** Returns the config overlay for a device, or 404 when none exists yet. */
  getDeviceConfig(deviceId: number): Observable<PvForecastingConfig> {
    return this.http.get<PvForecastingConfig>(`${this.baseUrl}/devices/${deviceId}`, {
      context: this.skipGlobalError(),
    });
  }

  createConfig(config: PvForecastingConfigCreate): Observable<PvForecastingConfig> {
    return this.http.post<PvForecastingConfig>(`${this.baseUrl}/devices`, config, {
      context: this.skipGlobalError(),
    });
  }

  updateConfig(
    deviceId: number,
    config: PvForecastingConfigUpdate,
  ): Observable<PvForecastingConfig> {
    return this.http.put<PvForecastingConfig>(`${this.baseUrl}/devices/${deviceId}`, config, {
      context: this.skipGlobalError(),
    });
  }

  deleteConfig(deviceId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/devices/${deviceId}`, {
      context: this.skipGlobalError(),
    });
  }

  private skipGlobalError(): HttpContext {
    return new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR, true);
  }
}
